import { useState, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Home,
  Package,
  FolderTree,
  Users,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Warehouse,
  Barcode,
  ClipboardList,
  ArrowLeftRight,
  Truck,
  Building2,
  ShoppingCart,
  Receipt,
  Monitor,
  Store,
  UserCircle,
  AlertTriangle,
  Tag,
  ShieldCheck,
  CreditCard,
  Webhook,
  Wrench,
  BarChart2,
  ScrollText,
  Settings2,
  RefreshCcw,
  Upload,
  User,
  PackageSearch,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/store";
import { useLogout } from "@/lib/auth/use-logout";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { KeyboardHelpDialog } from "@/components/ui/keyboard-help-dialog";

const ROUTE_LABELS: Record<string, string> = {
  "/": "Trang chủ",
  "/pos": "Bán hàng",
  "/orders": "Đơn hàng",
  "/products": "Sản phẩm",
  "/products/new": "Thêm mới",
  "/categories": "Danh mục",
  "/inventory": "Tồn kho",
  "/inventory/serials": "Tra serial",
  "/inventory/imports": "Nhập hàng",
  "/inventory/imports/new": "Tạo phiếu",
  "/inventory/transfers": "Chuyển kho",
  "/repair": "Sửa chữa",
  "/repair/new": "Tạo mới",
  "/warranty": "Bảo hành",
  "/warranty/new": "Tạo mới",
  "/customers": "Khách hàng",
  "/customers/new": "Thêm mới",
  "/debts": "Công nợ",
  "/debts/overdue": "Quá hạn",
  "/admin": "Quản trị",
  "/admin/coupons": "Mã giảm giá",
  "/admin/workstations": "Máy POS",
  "/admin/store": "Cửa hàng",
  "/admin/sepay": "SePay",
  "/admin/webhooks": "Webhook Log",
  "/admin/warehouses": "Kho hàng",
  "/admin/suppliers": "NCC",
  "/admin/users": "Người dùng",
  "/admin/import": "Import dữ liệu",
  "/admin/general": "Cài đặt chung",
  "/admin/inventory": "Tồn kho",
  "/admin/inventory/rebuild": "Rebuild tồn kho",
  "/admin/audit-log": "Nhật ký kiểm toán",
  "/reports": "Báo cáo",
  "/reports/revenue": "Doanh thu",
  "/reports/revenue/daily": "Theo ngày",
  "/reports/revenue/by-cashier": "Theo thu ngân",
  "/reports/revenue/by-payment-method": "Thanh toán",
  "/reports/revenue/by-order-type": "Lẻ / Sỉ",
  "/reports/revenue/gross-profit": "Lợi nhuận gộp",
  "/reports/inventory": "Tồn kho",
  "/reports/inventory/stock-value": "Giá trị tồn",
  "/reports/inventory/low-stock": "Sắp hết hàng",
  "/reports/inventory/slow-moving": "Chậm luân chuyển",
  "/reports/inventory/transactions": "Nhật ký kho",
  "/reports/inventory/import-history": "Lịch sử nhập",
  "/reports/customers": "Khách hàng",
  "/reports/customers/top": "Top khách hàng",
  "/reports/customers/overdue-aging": "Công nợ (aging)",
  "/reports/customers/acquisition": "Khách hàng mới",
  "/reports/staff": "Nhân viên",
  "/reports/staff/cashier": "Doanh thu của tôi",
  "/reports/staff/technician": "Kỹ thuật viên",
  "/account": "Tài khoản",
  "/bao-hanh": "Tra cứu bảo hành",
  "/wholesale": "Bán sỉ",
};

function Breadcrumbs({ pathname }: { pathname: string }) {
  if (pathname === "/login") return null;
  const segments = pathname.split("/").filter(Boolean);
  const crumbs: { path: string; label: string }[] = [
    { path: "/", label: ROUTE_LABELS["/"] },
  ];
  let acc = "";
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    acc += "/" + seg;
    const label = ROUTE_LABELS[acc];
    if (label) {
      crumbs.push({ path: acc, label });
    } else if (seg === "edit") {
      crumbs.push({ path: acc, label: "Chỉnh sửa" });
    } else if (seg === "new") {
      crumbs.push({ path: acc, label: "Tạo mới" });
    } else {
      crumbs.push({ path: acc, label: "Chi tiết" });
    }
  }
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
      <ol className="flex min-w-0 items-center gap-1 overflow-hidden">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={c.path} className="flex min-w-0 items-center gap-1">
              {idx > 0 && (
                <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
              )}
              {isLast ? (
                <span className="truncate font-medium text-foreground">{c.label}</span>
              ) : (
                <Link
                  to={c.path}
                  className="truncate text-muted-foreground hover:text-foreground"
                >
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  adminOnly?: boolean;
  roles?: string[];
}

interface NavGroup {
  label: string;
  icon: ReactNode;
  roles?: string[];
  items: NavItem[];
}

const NAV: NavItem[] = [
  { label: "Bán hàng", to: "/pos", icon: <ShoppingCart size={18} />, roles: ["cashier", "manager", "admin"] },
  { label: "Bán sỉ", to: "/wholesale", icon: <PackageSearch size={18} />, roles: ["cashier", "manager", "admin"] },
  { label: "Đơn hàng", to: "/orders", icon: <Receipt size={18} /> },
  { label: "Trang chủ", to: "/", icon: <Home size={18} /> },
  { label: "Sản phẩm", to: "/products", icon: <Package size={18} /> },
  { label: "Danh mục", to: "/categories", icon: <FolderTree size={18} /> },
  { label: "Tồn kho", to: "/inventory", icon: <Warehouse size={18} /> },
  { label: "Tra serial", to: "/inventory/serials", icon: <Barcode size={18} /> },
  { label: "Nhập hàng", to: "/inventory/imports", icon: <ClipboardList size={18} />, roles: ["admin", "manager", "warehouse"] },
  { label: "Chuyển kho", to: "/inventory/transfers", icon: <ArrowLeftRight size={18} />, roles: ["admin", "manager"] },
  { label: "Sửa chữa", to: "/repair", icon: <Wrench size={18} />, roles: ["technician", "manager", "admin"] },
  { label: "Bảo hành", to: "/warranty", icon: <ShieldCheck size={18} />, roles: ["cashier", "manager", "admin", "technician"] },
  { label: "Khách hàng", to: "/customers", icon: <UserCircle size={18} />, roles: ["cashier", "manager", "admin"] },
  { label: "Công nợ quá hạn", to: "/debts/overdue", icon: <AlertTriangle size={18} />, roles: ["manager", "admin"] },
  { label: "Mã giảm giá", to: "/admin/coupons", icon: <Tag size={18} />, roles: ["manager", "admin"] },
  { label: "Máy POS", to: "/admin/workstations", icon: <Monitor size={18} />, adminOnly: true },
  { label: "Cửa hàng", to: "/admin/store", icon: <Store size={18} />, adminOnly: true },
  { label: "SePay", to: "/admin/sepay", icon: <CreditCard size={18} />, adminOnly: true },
  { label: "Webhook Log", to: "/admin/webhooks", icon: <Webhook size={18} />, roles: ["admin", "manager"] },
  { label: "Kho hàng", to: "/admin/warehouses", icon: <Building2 size={18} />, adminOnly: true },
  { label: "NCC", to: "/admin/suppliers", icon: <Truck size={18} />, roles: ["admin", "manager"] },
  { label: "Người dùng", to: "/admin/users", icon: <Users size={18} />, adminOnly: true },
  { label: "Import dữ liệu", to: "/admin/import", icon: <Upload size={18} />, adminOnly: true },
  // Phase 09 — hardening admin tools
  { label: "Cài đặt chung", to: "/admin/general", icon: <Settings2 size={18} />, adminOnly: true },
  { label: "Rebuild tồn kho", to: "/admin/inventory/rebuild", icon: <RefreshCcw size={18} />, adminOnly: true },
  { label: "Nhật ký kiểm toán", to: "/admin/audit-log", icon: <ScrollText size={18} />, adminOnly: true },
];

// Phase 08 — Reports nav groups
const REPORT_GROUPS: NavGroup[] = [
  {
    label: "Doanh thu",
    icon: <BarChart2 size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Theo ngày", to: "/reports/revenue/daily", icon: <BarChart2 size={15} /> },
      { label: "Theo thu ngân", to: "/reports/revenue/by-cashier", icon: <BarChart2 size={15} /> },
      { label: "Thanh toán", to: "/reports/revenue/by-payment-method", icon: <BarChart2 size={15} /> },
      { label: "Lẻ / Sỉ", to: "/reports/revenue/by-order-type", icon: <BarChart2 size={15} /> },
      { label: "Lợi nhuận gộp", to: "/reports/revenue/gross-profit", icon: <BarChart2 size={15} /> },
    ],
  },
  {
    label: "Tồn kho",
    icon: <Warehouse size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Giá trị tồn", to: "/reports/inventory/stock-value", icon: <Warehouse size={15} /> },
      { label: "Sắp hết hàng", to: "/reports/inventory/low-stock", icon: <Warehouse size={15} /> },
      { label: "Chậm luân chuyển", to: "/reports/inventory/slow-moving", icon: <Warehouse size={15} /> },
      { label: "Nhật ký kho", to: "/reports/inventory/transactions", icon: <Warehouse size={15} /> },
      { label: "Lịch sử nhập", to: "/reports/inventory/import-history", icon: <Warehouse size={15} /> },
    ],
  },
  {
    label: "Khách hàng",
    icon: <UserCircle size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Top khách hàng", to: "/reports/customers/top", icon: <UserCircle size={15} /> },
      { label: "Công nợ (aging)", to: "/reports/customers/overdue-aging", icon: <UserCircle size={15} /> },
      { label: "Khách hàng mới", to: "/reports/customers/acquisition", icon: <UserCircle size={15} /> },
    ],
  },
  {
    label: "Nhân viên",
    icon: <Users size={16} />,
    roles: ["manager", "admin", "cashier"],
    items: [
      { label: "Doanh thu của tôi", to: "/reports/staff/cashier", icon: <Users size={15} />, roles: ["cashier", "manager", "admin"] },
      { label: "Kỹ thuật viên", to: "/reports/staff/technician", icon: <Users size={15} />, roles: ["manager", "admin"] },
    ],
  },
];

const ROLE_LABEL: Record<string, string> = {
  admin: "Quản trị",
  manager: "Quản lý",
  cashier: "Thu ngân",
  warehouse: "Kho",
  sales: "Bán hàng",
  technician: "Kỹ thuật",
};

function NavLink({ item, pathname, onClick }: { item: NavItem; pathname: string; onClick: () => void }) {
  const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(item.to));
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {item.icon}
      {item.label}
      {active && <ChevronRight size={14} className="ml-auto" />}
    </Link>
  );
}

function ReportGroupNav({
  group,
  userRole,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  userRole: string;
  pathname: string;
  onNavigate: () => void;
}) {
  const visibleItems = group.items.filter(
    (item) => !item.roles || item.roles.includes(userRole),
  );
  // Hooks must run unconditionally (before any early return), so compute
  // initial open-state here and bail out only afterwards.
  const isAnyActive = visibleItems.some((item) => pathname.startsWith(item.to));
  const [open, setOpen] = useState(isAnyActive);

  if (visibleItems.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
          isAnyActive
            ? "text-primary font-medium"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )}
      >
        {group.icon}
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l pl-3">
          {visibleItems.map((item) => (
            <NavLink key={item.to} item={item} pathname={pathname} onClick={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent transition-colors cursor-pointer"
      >
        <span className="text-sm text-muted-foreground hidden md:inline">{user?.email}</span>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {ROLE_LABEL[user?.role ?? ""] ?? user?.role}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border bg-card shadow-lg py-1">
            <button
              onClick={() => { setOpen(false); void navigate({ to: "/account" }); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <User size={16} />
              Tài khoản
            </button>
            <div className="my-1 border-t" />
            <button
              onClick={() => { setOpen(false); logout.mutate(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors"
              disabled={logout.isPending}
            >
              <LogOut size={16} />
              Đăng xuất
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const userRole = user?.role ?? "";

  const visibleNav = NAV.filter((item) => {
    if (item.adminOnly && userRole !== "admin") return false;
    if (item.roles && user && !item.roles.includes(userRole)) return false;
    return true;
  });

  // Determine which report groups to show
  const showReports = ["manager", "admin", "cashier"].includes(userRole);
  const isReportPath = pathname.startsWith("/reports");

  return (
    <div className="flex min-h-screen bg-background">
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r bg-card transition-transform duration-200 lg:static lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center border-b px-4 font-semibold text-primary">
          Nhật Minh Store
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {visibleNav.map((item) => (
            <NavLink key={item.to} item={item} pathname={pathname} onClick={() => setOpen(false)} />
          ))}

          {/* Phase 08 — Reports section */}
          {showReports && (
            <div className="pt-2">
              <button
                onClick={() => setReportsOpen((o) => !o)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isReportPath
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <BarChart2 size={18} />
                <span className="flex-1 text-left">Báo cáo</span>
                {reportsOpen || isReportPath ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>

              {(reportsOpen || isReportPath) && (
                <div className="ml-2 mt-1 space-y-0.5">
                  {REPORT_GROUPS.map((group) => {
                    if (group.roles && !group.roles.includes(userRole)) return null;
                    return (
                      <ReportGroupNav
                        key={group.label}
                        group={group}
                        userRole={userRole}
                        pathname={pathname}
                        onNavigate={() => setOpen(false)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center gap-4 border-b bg-card px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </Button>

          <Breadcrumbs pathname={pathname} />

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {user && <UserMenu />}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>

      {/* Global keyboard help — ? opens dialog */}
      <KeyboardHelpDialog />
    </div>
  );
}
